/**
 * Keyboard input. Tracks held state (for variable-jump / continuous movement)
 * and per-step "just pressed" edges (for jump buffering and toggles).
 *
 * Input handling is deliberately kept clean + remappable so that, per the brief,
 * deterministic input replay could be layered on later without touching physics.
 */
export type Action = 'left' | 'right' | 'jump' | 'run' | 'duck' | 'up' | 'attack' | 'dash' | 'grab' | 'aimUp' | 'toggleDebug' | 'reset';

/**
 * Per-fixed-step command struct — the ONLY input the deterministic sim sees.
 * Holds RESOLVED values (not raw key edges): `runActive` is already the latch's
 * resolved state, so a future replay can inject snapshots directly and sidestep
 * the toggle latch entirely. The meta action (reset) is deliberately NOT here —
 * it is debug/UX, excluded from any recorded snapshot.
 */
export interface InputSnapshot {
  /** Resolved horizontal axis. */
  axisX: -1 | 0 | 1;
  /** Rising edge this step (jump buffer / coyote consume it). */
  jumpPressed: boolean;
  /** Held state this step (variable-jump release gate reads it). */
  jumpHeld: boolean;
  /** Resolved sprint state (latched via Shift toggle). */
  runActive: boolean;
  /** Held duck this step. */
  duck: boolean;
  /**
   * Rising edge of the "enter/up" action this step (Brief 011) — opening a DOOR reads
   * it. A press EDGE (not held) so holding E in a doorway warps exactly once, and the
   * destination door does not immediately re-fire. Bound to KeyE so it never collides
   * with the jump keys (Space / ↑ / W / Z stay jump).
   */
  up: boolean;
  /**
   * Rising edge of the COMBAT attack (directional sword + down-attack pogo). A press
   * EDGE (not held) so one tap = one swing; the Player gates it on attackEnabled +
   * cooldown. Bound to KeyJ so it never collides with movement/jump. Inert for non-COMBAT
   * archetypes (the verb flag is off → Player never reads it). Replay-relevant → packed.
   */
  attack: boolean;
  /**
   * Rising edge of the COMBAT dash-kill (a committed directional air-dash with a damage
   * hitbox). Press EDGE; gated on dashEnabled + cooldown. Bound to KeyU (KeyK/L/M/F are
   * the game's panel shortcuts). Inert for non-COMBAT archetypes. Replay-relevant → packed.
   * DASH-PRECISION reuses this same press for its 8-way charge dash (the aim adds `aimUp`/duck).
   */
  dash: boolean;
  /**
   * HELD grip for the DASH-PRECISION stamina wall-climb (`grab`). While held + airborne +
   * beside a wall + stamina > 0, the player climbs. Bound to KeyC (KeyK/L/M/F = panels,
   * KeyShift = run, so a free letter). HELD (not an edge) so a grip persists. Inert for
   * archetypes without the climb verb. Replay-relevant → packed (bit 9).
   */
  grab: boolean;
  /**
   * HELD up-aim for the 8-way air-dash (DASH-PRECISION). The dash samples it at the press to
   * resolve an UP / up-diagonal direction (the horizontal comes from axisX, DOWN from duck).
   * Derived from the held jump keys (W/↑/Space/Z) on keyboard — "hold up + dash = dash up" —
   * OR the touch joystick's up sector. Inert unless a dash reads it. Replay-relevant → packed
   * (bit 10) so a recorded up-dash reproduces.
   */
  aimUp: boolean;
  /**
   * HELD fire for the RUN-AND-GUN gun (held auto-fire — Isaac, 2026-06-30). While held +
   * the shoot verb is on + the fire cooldown has elapsed, the Player emits a bullet, then
   * re-arms the cooldown (the spray rate cap). HELD (not an edge) so a continuous hold
   * auto-fires. Reuses the `attack` action (KeyJ held, OR the touch FIRE button held) so no
   * new KEY is spent — but it IS a new packed field (a held signal can't be reconstructed
   * from the `attack` press-edge), so it packs as bit 11. Inert unless the shoot verb is on
   * (every other archetype never reads it). Replay-relevant → packed (bit 11) so a recorded
   * run reproduces its bullets.
   */
  fireHeld: boolean;
}

const KEY_MAP: Record<string, Action> = {
  ArrowLeft: 'left', KeyA: 'left',
  ArrowRight: 'right', KeyD: 'right',
  Space: 'jump', ArrowUp: 'jump', KeyW: 'jump', KeyZ: 'jump',
  ShiftLeft: 'run', ShiftRight: 'run',
  ArrowDown: 'duck', KeyS: 'duck',
  KeyE: 'up', // Brief 011: enter a DOOR (separate from jump so it never conflicts)
  KeyJ: 'attack', // COMBAT: sword / down-attack pogo (off the jump + movement keys)
  KeyU: 'dash',   // COMBAT dash-kill + DASH-PRECISION 8-way dash (KeyK/L/M/F = panel shortcuts)
  KeyC: 'grab',   // DASH-PRECISION: grip a wall to stamina-climb (held; free letter)
  Backquote: 'toggleDebug',
  KeyR: 'reset',
};

export class Input {
  private held = new Set<Action>();
  private justPressed = new Set<Action>();
  /** Touch/virtual buttons (mobile controls) — merged into the same command stream
   *  as the keyboard, so the deterministic sim never knows the difference. */
  private virtual = new Set<Action>();
  /** Latched sprint state. Run is a TOGGLE (tap Shift) so sprinting never needs
   *  a 3-key hold — dodges keyboard ghosting (e.g. Shift+D+Space rollover). */
  private runLatched = false;

  // Bound handler refs so destroy() can remove them (the editor's playtest, Brief
  // 004, constructs a fresh Input per play and tears it down on exit — E4).
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onBlur: () => void;

  constructor(private target: Window = window) {
    this.onKeyDown = (e) => {
      const a = KEY_MAP[e.code];
      if (!a) return;
      // Stop the page from scrolling on arrows/space.
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) e.preventDefault();
      if (!this.held.has(a)) {
        this.justPressed.add(a); // rising edge
        if (a === 'run') this.runLatched = !this.runLatched; // tap toggles sprint
      }
      this.held.add(a);
    };
    this.onKeyUp = (e) => {
      const a = KEY_MAP[e.code];
      if (a) this.held.delete(a);
    };
    // Drop held state if focus is lost (prevents "stuck running" after alt-tab).
    this.onBlur = () => { this.held.clear(); this.virtual.clear(); };
    target.addEventListener('keydown', this.onKeyDown);
    target.addEventListener('keyup', this.onKeyUp);
    target.addEventListener('blur', this.onBlur);
  }

  /** Remove all window listeners + clear state. The inverse of the constructor;
   *  call when retiring an Input (e.g. exiting a playtest) so no key listener leaks. */
  destroy(): void {
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
    this.held.clear();
    this.justPressed.clear();
  }

  isDown(a: Action): boolean { return this.held.has(a) || this.virtual.has(a); }
  pressed(a: Action): boolean { return this.justPressed.has(a); }

  /** Effective sprint state (latched via Shift toggle / mobile RUN button). */
  runActive(): boolean { return this.runLatched; }

  // ── virtual (touch) input ─────────────────────────────────────────────────
  /** Press/release a virtual button (on-screen mobile control). Held buttons stay
   *  in `virtual` until released, exactly like a held key. */
  setVirtual(a: Action, down: boolean): void {
    if (down) { if (!this.virtual.has(a)) { this.virtual.add(a); this.justPressed.add(a); } }
    else { this.virtual.delete(a); }
  }

  /** Flip the sprint latch (mobile RUN toggle). Returns the new state for the UI. */
  toggleRun(): boolean { this.runLatched = !this.runLatched; return this.runLatched; }
  /** Force the sprint latch (mobile auto-run default). */
  setRunLatched(on: boolean): void { this.runLatched = on; }

  /** Horizontal axis from held left/right. */
  axisX(): -1 | 0 | 1 { return ((this.isDown('right') ? 1 : 0) - (this.isDown('left') ? 1 : 0)) as -1 | 0 | 1; }

  /**
   * Resolve the live keyboard into a per-step command struct. Call ONCE per fixed
   * step, BEFORE endStep() (it must see this step's `jumpPressed` rising edge).
   * This is the seam a future replay swaps out — feed recorded snapshots and the
   * sim never touches the keyboard.
   */
  snapshot(): InputSnapshot {
    return {
      axisX: this.axisX(),
      jumpPressed: this.pressed('jump'),
      jumpHeld: this.isDown('jump'),
      runActive: this.runLatched,
      duck: this.isDown('duck'),
      up: this.pressed('up'),
      attack: this.pressed('attack'),
      dash: this.pressed('dash'),
      grab: this.isDown('grab'), // HELD grip (stamina-climb)
      // HELD up-aim: the jump keys double as the dash's up-aim ("hold up + dash"), OR the
      // touch joystick's up sector (a dedicated 'aimUp' virtual). Only read at a dash press.
      aimUp: this.isDown('jump') || this.isDown('aimUp'),
      // HELD fire (RUN-AND-GUN held auto-fire): the `attack` action HELD — KeyJ held on keyboard,
      // OR the touch FIRE button held (both feed the 'attack' action). Only read when the shoot
      // verb is on.
      fireHeld: this.isDown('attack'),
    };
  }

  /** Clear per-step edges. Call once at the END of each fixed update. */
  endStep(): void { this.justPressed.clear(); }
}
